/**
 * Notification context for managing in-page notifications.
 *
 * Provides a way to show notifications from anywhere in the app.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Notification } from '@/components/Notification';
import { VIEW_ONLY_MESSAGE } from '@/api/client';
import { notificationContainerStyle } from '@/utils/notificationLayout';

/** Prefix of the per-edit save tick; a new one replaces the last instead of stacking. */
export const SAVED_PREFIX = 'Saved';

interface NotificationData {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  showSpinner?: boolean;
  /** Replaces the default type glyph (e.g. the call chip). */
  icon?: ReactNode;
  /** A new notification with the same key replaces the earlier one instead of stacking. */
  key?: string;
  action?: { label: string; onClick: () => void };
}

interface NotificationContextValue {
  notify: (message: string, type?: NotificationData['type'], options?: { duration?: number; showSpinner?: boolean; icon?: ReactNode; key?: string; action?: { label: string; onClick: () => void } }) => void;
  notifyInfo: (message: string, showSpinner?: boolean, duration?: number) => void;
  notifySuccess: (message: string) => void;
  notifyWarning: (message: string) => void;
  notifyError: (message: string) => void;
  dismissSpinner: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const addNotification = useCallback((incoming: Omit<NotificationData, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    // A refused view-only write reaches here twice — once from the app-level
    // notice, once from whichever caller's catch reports its own error, often
    // with a prefix. Show it once, as a warning rather than a failure.
    const isViewOnly = incoming.message.includes(VIEW_ONLY_MESSAGE);
    const notification = isViewOnly
      ? { ...incoming, message: VIEW_ONLY_MESSAGE, type: 'warning' as const, duration: 6000 }
      : incoming;
    setNotifications((prevAll) => {
      const prev = isViewOnly ? prevAll.filter(n => n.message !== VIEW_ONLY_MESSAGE)
        : incoming.message.startsWith(SAVED_PREFIX) ? prevAll.filter(n => !n.message.startsWith(SAVED_PREFIX))
        : incoming.key ? prevAll.filter(n => n.key !== incoming.key)
        : prevAll;
      // If showing a success/error/warning notification, dismiss any loading (info with spinner) notifications
      if ((notification.type === 'success' || notification.type === 'error' || notification.type === 'warning')) {
        const filtered = prev.filter(n => !(n.type === 'info' && n.showSpinner));
        return [...filtered, { ...notification, id }];
      }
      return [...prev, { ...notification, id }];
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, type: NotificationData['type'] = 'info', options?: { duration?: number; showSpinner?: boolean; icon?: ReactNode; key?: string; action?: { label: string; onClick: () => void } }) => {
      addNotification({
        icon: options?.icon,
        key: options?.key,
        action: options?.action,
        message,
        type,
        duration: options?.duration ?? 3000,
        showSpinner: options?.showSpinner ?? false,
      });
    },
    [addNotification]
  );

  const notifyInfo = useCallback(
    (message: string, showSpinner = true, duration?: number) => {
      addNotification({
        message,
        type: 'info',
        duration: duration ?? (showSpinner ? 0 : 3000),
        showSpinner,
      });
    },
    [addNotification]
  );

  const notifySuccess = useCallback(
    (message: string) => {
      addNotification({
        message,
        type: 'success',
        duration: 3000,
        showSpinner: false,
      });
    },
    [addNotification]
  );

  const notifyWarning = useCallback(
    (message: string) => {
      addNotification({
        message,
        type: 'warning',
        duration: 4000,
        showSpinner: false,
      });
    },
    [addNotification]
  );

  const notifyError = useCallback(
    (message: string) => {
      addNotification({
        message,
        type: 'error',
        duration: 5000,
        showSpinner: false,
      });
    },
    [addNotification]
  );

  const dismissSpinner = useCallback(() => {
    setNotifications(prev => prev.filter(n => !(n.type === 'info' && n.showSpinner)));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notify,
        notifyInfo,
        notifySuccess,
        notifyWarning,
        notifyError,
        dismissSpinner,
      }}
    >
      {children}

      {/* Notification container (fixed position) */}
      <div style={notificationContainerStyle()}>
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            id={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            showSpinner={notification.showSpinner}
            icon={notification.icon}
            action={notification.action}
            onDismiss={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}
